import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, AlertTriangle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { PageHead, STATUSES, Stat, StatusBadge, money, useApi } from "@/components/shop/common";
import { useAuth } from "@/context/AuthContext";

const BOARD = STATUSES.filter((s) => s.key !== "invoiced");

const JobCard = ({ w, onMove, tech }) => (
  <div className="bg-card border border-border rounded-sm p-4 hover:border-slate-600 transition-colors" data-testid={`board-card-${w.id}`}>
    <Link to={`/app/work-orders/${w.id}`} className="block group" data-testid={`board-card-link-${w.id}`}>
      <p className="font-mono text-[11px] text-primary">WO #{w.number}</p>
      <p className="font-semibold text-sm mt-1 group-hover:text-primary transition-colors">{w.vehicle_label}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{w.customer?.name}</p>
      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{w.complaint}</p>
    </Link>
    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border gap-2">
      <span className="font-display text-lg font-bold">{money(w.total)}</span>
      <select value={w.status} onChange={(e) => onMove(w, e.target.value)} data-testid={`board-move-${w.id}`}
        className="bg-[#0B0F17] border border-input text-xs rounded-sm px-1.5 py-1 max-w-[120px]">
        {STATUSES.filter((s) => !tech || s.key !== "invoiced").map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>
    </div>
    {w.technician && <p className="text-[11px] text-muted-foreground mt-2 font-mono">TECH: {w.technician.name}</p>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const tech = user?.shop_role === "technician";
  const [d, load] = useApi("/dashboard");
  const move = async (w, status) => {
    try { await api.put(`/work-orders/${w.id}`, { status }); toast.success(`WO #${w.number} → ${status.replace("_", " ")}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHead eyebrow="Shop floor" title="Shop board">
        {!tech && <Button asChild className="rounded-sm hover:bg-orange-600 hover:text-white" data-testid="dashboard-new-wo-btn">
          <Link to="/app/work-orders?new=1"><Plus className="h-4 w-4 mr-1" /> New work order</Link>
        </Button>}
      </PageHead>
      {d && (
        <>
          <div className={`grid grid-cols-2 ${tech ? "lg:grid-cols-2" : "lg:grid-cols-5"} gap-4 mb-8`}>
            <Stat label="Open jobs" value={d.open_count} testid="stat-open" />
            {!tech && <Stat label="Invoiced this month" value={money(d.revenue_month)} accent="text-emerald-400" testid="stat-revenue" />}
            {!tech && <Stat label="Done, not billed" value={money(d.unbilled)} accent="text-amber-400" testid="stat-unbilled" />}
            <Stat label="Tech hours (7d)" value={d.week_hours} testid="stat-hours" />
            {!tech && <Stat label="Low stock" value={d.low_stock.length} accent={d.low_stock.length ? "text-red-400" : ""} testid="stat-low-stock" />}
          </div>
          {(d.low_stock.length > 0 || d.active_clocks.length > 0) && (
            <div className="flex flex-wrap gap-3 mb-8">
              {d.low_stock.length > 0 && (
                <Link to="/app/inventory" className="flex items-center gap-2 text-sm border border-red-500/40 bg-red-500/10 text-red-300 px-3 py-2 rounded-sm" data-testid="low-stock-alert">
                  <AlertTriangle className="h-4 w-4" /> Low: {d.low_stock.slice(0, 4).map((i) => i.name).join(", ")}
                </Link>
              )}
              {d.active_clocks.length > 0 && (
                <Link to="/app/technicians" className="flex items-center gap-2 text-sm border border-orange-500/40 bg-orange-500/10 text-orange-300 px-3 py-2 rounded-sm" data-testid="active-clocks-alert">
                  <Clock className="h-4 w-4" /> {d.active_clocks.length} tech(s) on the clock
                </Link>
              )}
            </div>
          )}
          <div className="grid grid-flow-col auto-cols-[minmax(220px,1fr)] xl:grid-flow-row xl:grid-cols-5 gap-4 overflow-x-auto pb-4" data-testid="status-board">
            {BOARD.map((s) => {
              const items = d.work_orders.filter((w) => w.status === s.key);
              return (
                <div key={s.key} className="bg-[#0B0F17]/60 border border-border rounded-sm p-3 min-h-[300px]" data-testid={`board-col-${s.key}`}>
                  <div className="flex items-center justify-between mb-3 px-1">
                    <StatusBadge status={s.key} />
                    <span className="font-mono text-xs text-muted-foreground">{items.length}</span>
                  </div>
                  <div className="flex flex-col gap-3">
                    {items.map((w) => <JobCard key={w.id} w={w} onMove={move} tech={tech} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
